import React, { useEffect } from "react";
import DataTable from "react-data-table-component";
import PageWrapper from "../../../components/layouts/PageWrapper";
import Search from "../../../components/layouts/Search";
import { useLocation, useNavigate } from "react-router-dom";
import { productTagColumns } from "../../../helpers/columns/product/product-tag.columns";
import usePagination from "../../../hooks/usePagination";
import useSearch from "../../../hooks/useSearch";
import {
  useGetAllUserRolesQuery,
  useDeleteUserRoleMutation,
} from "../../../states/user-management/user-role.api";
import { useErrorHandling } from "../../../hooks/useErrorHandling";
import { castTypeArr } from "../../../utils/general/cast-type";
import { TFailResponse } from "../../../types/general/TFailResponse";
import { useDidMountEffect } from "../../../hooks/useDidMountEffect";
import { TProductTag } from "../../../types/product/TProductTag";
import { USER_ROLE } from "../../../utils/constants/user-role.constants";
import { DeletePopup } from "../../../helpers/popups/DeletePopup";
import TableSkeleton from "../../../components/layouts/TableSkeleton";
import { TDeleteSuccesResponse } from "../../../types/general/TDeleteSuccessResponse";
import { Toast } from "../../../helpers/popups/Toast";
import AuthComponent from "@components/auth/component";
import useGetPermission from "@hooks/common/useGetPermission";
import { TUserRole } from "../../../types/user-management/TUserRole";
import { userRoleColumns } from "../../../helpers/columns/user-management/user-role.columns";

const UserRolePage = () => {
  const { hasAccess } = useGetPermission();
  const { page, limit, handlePageChange, handlePerRowsChange } =
    usePagination();
  const { search, setSearch } = useSearch();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    data,
    isFetching,
    error: getAllError,
    refetch,
  } = useGetAllUserRolesQuery({
    page,
    limit,
    user_role_name: search,
  });
  const [deleteUserRole, { error: deleteError, isLoading }] =
    useDeleteUserRoleMutation();



  // a custom hook that will handle fetch/mutation errors
  useErrorHandling(...castTypeArr<TFailResponse>([getAllError, deleteError]));

  useDidMountEffect(() => {
    refetch();
  }, [page, limit, search]);

  useEffect(() => {
    if (location.state?.reload) {
      refetch();
    }
  }, [location.state?.reload]);

  const editHandler = (userRole: any) => {
    navigate(`${USER_ROLE.BASE_PATH}${userRole.user_role_id}/edit`, {
      state: {
        userRole,
      },
    });
  };

  const deleteHandler = async (id: number | string) => {
    try {
      const { isConfirmed } = await DeletePopup.fire();

      if (isConfirmed) {
        const res = await deleteUserRole(id);

        const successData = "data" in res ? res.data : null;
        const { success, message } = successData as TDeleteSuccesResponse;

        if (success) {
          Toast.fire({
            icon: "success",
            title: message,
          });

          refetch();
        }
      }
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <AuthComponent action="view" module="User Management" subModule="User Role" page>
      <Search setSearch={setSearch} />
      <PageWrapper
        addPath={"/user-management/user-roles/add"}
        addButtonTitle="Add New User Role"
        title="User Role"
        hasCreatePermission={hasAccess("create", "User Management", "User Role")}
      >
        <DataTable
          keyField="user_role_id"
          responsive
          striped
          pagination
          paginationServer
          //@ts-ignore
          paginationTotalRows={data?.paginator?.totalItems}
          progressPending={isFetching}
          onChangeRowsPerPage={handlePerRowsChange}
          onChangePage={handlePageChange}
          noDataComponent={<TableSkeleton row={10} col={3} />}
          progressComponent={<TableSkeleton row={10} col={3} />}
          //@ts-ignore
          columns={userRoleColumns(editHandler, deleteHandler)}
          //@ts-ignore
          data={data?.data}
        />
      </PageWrapper>
    </AuthComponent>
  );
};

export default UserRolePage;
